<template>
    <div
        ref="carouselHolder"
        class="content-widget carousel-area"
    >
        <div 
            class="gallery"
            ref="gallery"
        >
            <div
                class="slideset"
                :class="{'resize': resize}"
                ref="slider"
                :style="{ 'transform':'translateX('+ translate +'px)' }"
            >
                <div
                    v-for="(image, index) in data.images"
                    :key="index"
                    class="slide"
                    :class="{ 'active': index === currentIndex }"
                    v-hammer:swipe.horizontal="!hasImageComparison(image.url) ? onSwipe : null"
                >
                    <img-comparison-slider
                        v-if="hasImageComparison(image.url)"
                        class="slider-split-line"
                    >
                        <img
                            slot="first"
                            draggable=false
                            :ref="'image' + index"
                            :id="'images' + index"
                            :alt="image.description"
                            :src="image.url[0]"
                            :style="refreshValue && setSize(index)"
                            @load="onLoaded"
                        />
                        <img
                            slot="second"
                            draggable=false
                            :ref="'image' + index"
                            :id="'images' + index"
                            :alt="image.description"
                            :src="image.url[1]"
                            :style="refreshValue && setSize(index)"
                            @load="onLoaded"
                        />
                        <div 
                            slot="handle" 
                            class="handle-image d-flex"
                        >
                            <v-icon class="mdi left">
                                mdi-arrow-left
                            </v-icon>
                            <v-icon class="mdi right">
                                mdi-arrow-right
                            </v-icon>
                        </div>
                    </img-comparison-slider>

                    <img
                        v-else
                        draggable="false"
                        :ref="'image'+index"
                        :id="'images'+index"
                        :alt="image.description"
                        :src="image.url"
                        :style="refreshValue && setSize(index)"
                        @load="onLoaded"
                    >
                </div>
            </div>
            <div
                v-if="hasPagination"
                class="pagination" 
                v-hammer:swipe.horizontal="onSwipe"
            >
                <div class="pagination-wrapper">
                    <div class="btn btn__prev-holder">
                        <a
                            href="#"
                            class="btn-prev"
                            :class="{ 'disabled': currentIndex === 0 }"
                            @click.prevent="numSlide(currentIndex-1)"
                            @mousedown="startMovePrev"
                            @mouseleave="stopMove"
                            @mouseup="stopMove"
                            @touchstart="startMovePrev"
                            @touchend="stopMove"
                            @touchcancel="stopMove"
                        >&#60;</a>
                    </div>
                    <div
                        class="slider"
                        :style="{ 'transform':'translateX(-'+ (currentIndex/(data.images ? data.images.length : 0)*100) +'%)' }"
                    >
                        <div
                            v-for="(image, index) in data.images"
                            :key="'img'+index"
                            class="slide"
                            :class="{ 'active': index === currentIndex }"
                        >
                            <a
                                href="#"
                                @click.prevent="numSlide(index)"
                                :class="{ 'smallLeft': index === currentIndex-1, 'smallestLeft': index === currentIndex-2,'smallRight': index === currentIndex+1, 'smallestRight': index === currentIndex+2,'hiddenImage': index > currentIndex+2 || index < currentIndex-2 }"
                            >
                                <img
                                    :alt="image.description"
                                    :src="image.preview"
                                    class="slide-rounded"
                                >
                            </a>
                        </div>
                    </div>
                    <div class="btn btn__next-holder">
                        <a
                            href="#"
                            class="btn-next"
                            :class="{ 'disabled': currentIndex === (data.images ? data.images.length-1 : 0) }"
                            @click.prevent="numSlide(currentIndex+1)"
                            @mousedown="startMoveNext"
                            @mouseleave="stopMove"
                            @mouseup="stopMove"
                            @touchstart="startMoveNext"
                            @touchend="stopMove"
                            @touchcancel="stopMove"
                        >&#62;</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
    import Http from '@/api/data';
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        name: 'Carousel',
        props: [
            'link',
            'active',
            'standalone',
            'isOpenedSidebar'
        ],
        data: () => ({
            data: {},
            isAnim: false,
            currentIndex: 0,
            translate: 0,
            tap: 0,
            zoomIn: false,
            maxTop: 0,
            maxLeft: 0,
            currentTop: 0,
            currentLeft: 0,
            widget: null,
            panStart: null,
            resize: false,
            ratio: 1,
            refreshValue: 1,
            desktop: false
        }),
        computed: {
            ...mapGetters([
                'isOfflineMode',
                'isReviewUse',
                'language',
                'protocol',
                'startedWidget'
            ]),
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
            hasPagination() {
                return this.data.images ? this.data.images.length > 1 : false;
            }
        },
        mounted() {
            this.desktop = window.innerWidth > 1023;
            this.render();
            window.addEventListener('resize', this.onResize);
        },
        methods: {
            ...mapActions([
                'setIsLoading'
            ]),
            init() {
                document.addEventListener('keydown', (e) => {
                    switch (e.keyCode) {
                        case 39:
                            this.numSlide(this.currentIndex + 1);
                            break;
                        case 37:
                            this.numSlide(this.currentIndex - 1);
                            break;
                    }
                });

                // detect device type
                const isTouchDevice = /Windows Phone/.test(navigator.userAgent) || ('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch;

                const hammer = new Hammer.Manager(this.$refs.slider);
                const pan = new Hammer.Pan({pointers: isTouchDevice ? 2 : 1});
                const doubletap = new Hammer.Tap({event: 'doubletap', taps: 2});

                hammer.add([pan, doubletap]);

                hammer.on('panstart', (e) => {
                    this.onPanStart(e);
                }).on('panmove', (e) => {
                        this.onPanMove(e);
                }).on('panend', () => {
                        this.onPanEnd();
                }).on('doubletap', () => {
                    this.onTap();
                });
                this.setIsLoading(false);
            },
            hasImageComparison(imageUrl) {
                return Array.isArray(imageUrl);
            },
            startMoveNext() {
                this.interval = setInterval(() => this.numSlide(this.currentIndex + 1), 1000);
            },
            stopMove() {
                clearInterval(this.interval);
            },
            startMovePrev() {
                this.interval = setInterval(() => this.numSlide(this.currentIndex - 1), 1000);
            },
            setSize(index) {
                if (this.desktop) {
                    let imageWidth = this.$refs.gallery.offsetWidth;
                    let imageHeight = imageWidth / this.ratio;
                    if (imageHeight < this.$refs.gallery.offsetHeight) {
                        imageHeight = this.$refs.gallery.offsetHeight;
                        imageWidth = imageHeight * this.ratio;
                    }
                    return {
                        transform: 'scale(' + (index === this.currentIndex && this.zoomIn ? 2 : 1) + ')',
                        left: this.currentLeft + 'px',
                        top: this.currentTop + 'px',
                        width: imageWidth + 'px',
                        height: imageHeight + 'px',
                        marginTop: (this.$refs.gallery.offsetHeight - imageHeight) / 2 + 'px',
                        marginLeft: (this.$refs.gallery.offsetWidth - imageWidth) / 2 + 'px'
                    };
                } else
                    return {
                        transform: 'scale(' + (index === this.currentIndex && this.zoomIn ? 2 : 1) + ')',
                        left: this.currentLeft + 'px',
                        top: this.currentTop + 'px'
                    };
            },
            onResize() {
                this.resetZoom();
                this.desktop = window.innerWidth > 1023;
                if (this.$refs.slider && this.$refs.slider.offsetWidth) {
                    this.resize = true;
                    this.translate = -this.currentIndex * this.$refs.slider.offsetWidth;
                    this.refreshValue++;
                    this.onLoaded();
                }
            },
            onLoaded() {
                this.maxTop = this.$refs.slider.offsetHeight / 2;
                this.maxLeft = this.$refs.slider.offsetWidth / 2;
                if (this.ratio === 1)
                    this.ratio = this.getRatio();
            },
            getRatio() {
                if (!this.$refs['image0'])
                    return;

                const image = this.$refs['image0'][0];
                if (image.naturalWidth)
                    return image.naturalWidth / image.naturalHeight;
                else 
                    return image.width / image.height;
            },
            onSwipe(e) {
                if (!this.zoomIn)
                    if (e.deltaX < 0)
                        this.numSlide(this.currentIndex + 1);
                    else
                        this.numSlide(this.currentIndex - 1);
            },
            onTap() {
                this.toggleTap();
            },
            onPanStart(e) {
                this.panStart = {
                    x: e.srcEvent.pageX,
                    y: e.srcEvent.pageY,
                    offset: {
                        left: this.currentLeft,
                        top: this.currentTop
                    }
                };
            },
            onPanEnd() {
                this.panStart = null;
            },
            onPanMove(e) {
                if (this.zoomIn) {
                    let top = this.panStart.offset.top + e.deltaY;
                    let left = this.panStart.offset.left + e.deltaX;
                    if (this.maxTop > Math.abs(top))
                        this.currentTop = top;
                    else
                        this.currentTop = this.maxTop * (top / Math.abs(top));

                    if (this.maxLeft > Math.abs(left))
                        this.currentLeft = left;
                    else
                        this.currentLeft = this.maxLeft * (left / Math.abs(left));
                }
            },
            toggleTap() {
                if (this.isAnim)
                    return;
                this.zoomIn = !this.zoomIn;
                this.currentTop = 0;
                this.currentLeft = 0;
            },
            numSlide(i) {
                if (this.isAnim)
                    return;
                if (i < 0)
                    i = 0;
                else if (i > this.data.images.length - 1)
                    i = this.data.images.length - 1;
                this.isAnim = true;
                this.currentIndex = i;
                this.resetZoom();
                setTimeout(() => {
                    this.isAnim = false;
                }, 800);
            },
            resetZoom() {
                this.zoomIn = false;
                this.currentTop = 0;
                this.currentLeft = 0;
            },
            render() {
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/' + this.startedWidget + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                if (this.isOfflineMode && !urlLink.includes('&offline'))
                    urlLink += '&offline';
                Http.getData(urlLink).then(payload => {
                    if (payload) {
                        if (payload.language)
                            this.$i18n.locale = payload.language;
                        this.data = payload;
                        this.init();
                        if (this.standalone)
                            EventBus.$emit('updateSidebar', payload, false);
                        EventBus.$emit('trackWidget', payload.tracking);
                    }
                }).catch(error => {
                    EventBus.$emit('setErrorState', true, error);
                });
            },
        },
        watch: {
            active(val) {
                if (val)
                    this.onResize();
            },
            currentIndex(val) {
                this.resize = false;
                this.translate = -val * this.$refs.gallery.offsetWidth;
            },
            isOpenedSidebar() {
                setTimeout(() => {
                    for (let i = 0; i < this.data.images.length; i++) {
                        const elementStyle = document.getElementById('images' + i).style;
                        const style = this.setSize(i);
                        this.translate = -this.currentIndex * this.$refs.gallery.offsetWidth;

                        elementStyle.left = style.left;
                        elementStyle.top = style.top;

                        if (style.width)
                            elementStyle.width = style.width;
                        if (style.height)
                            elementStyle.height = style.height;
                        if (style.marginTop)
                            elementStyle.marginTop = style.marginTop;
                        if (style.marginLeft)
                            elementStyle.marginLeft = style.marginLeft;
                        if (style.transform)
                            elementStyle.transform = style.transform;
                    }
                }, 500);
            }
        }
    };
</script>

<style lang="scss">
    @import "@/assets/scss/_common.scss";
    @import "@/assets/scss/components/_carousel.scss";
</style>


// WEBPACK FOOTER //
// src/views/CarouselWidget.vue